import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Trash2, Briefcase, Power, PowerOff } from 'lucide-react';

const Personal = () => {
  const navigate = useNavigate();
  const [personal, setPersonal] = useState([]);
  const [miRol, setMiRol] = useState('');
  
  const [formData, setFormData] = useState({
    nombres: '', apellidos: '', email: '', password: '', rol: 'Docente'
  });

  useEffect(() => { 
    cargarRolLocal();
    cargarPersonal(); 
  }, []);

  const cargarRolLocal = () => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');
    try {
      const payloadBase64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const decodedPayload = JSON.parse(window.atob(payloadBase64));
      setMiRol(decodedPayload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || decodedPayload.role || '');
    } catch (e) {
      console.error(e);
    }
  };

  const cargarPersonal = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Personal`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPersonal(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Personal`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setFormData({ nombres: '', apellidos: '', email: '', password: '', rol: 'Docente' });
        cargarPersonal();
      } else {
        const error = await res.text();
        alert(`Error: ${error}`);
      }
    } catch (e) { console.error(e); }
  };

  const handleToggleEstado = async (id, estadoActual) => {
    const accion = estadoActual ? 'desactivar' : 'activar';
    if(!window.confirm(`¿Seguro que deseas ${accion} el acceso de este usuario?`)) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Personal/toggle/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) cargarPersonal();
      else alert(await res.text());
    } catch (e) { console.error(e); }
  };

  const handleEliminar = async (id) => {
    if(!window.confirm('¿Seguro que deseas ELIMINAR definitivamente este usuario? (Si tiene registros, se bloqueará la acción)')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Personal/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) cargarPersonal();
      else alert(await res.text());
    } catch (e) { console.error(e); }
  };

  // Permisos UI: El SuperAdmin recupera el control total junto a la Plana Mayor
  const puedeAdministrar = ['SUPERADMIN', 'ADMINCOLEGIO', 'DIRECTOR', 'ADMIN', 'ADMINISTRATIVO'].includes(miRol.toUpperCase());

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />

      <main className="flex-1 p-6 overflow-y-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Briefcase className="text-blue-600" /> Personal de la Institución
          </h1>
          <p className="text-slate-500 mt-1">Gestión de cuentas de acceso para el equipo administrativo y docente.</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Formulario (Oculto si no tiene permisos) */}
          {puedeAdministrar && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 h-fit">
              <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><UserPlus size={20}/> Nuevo Usuario</h2>
              <form onSubmit={handleGuardar} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">Nombres</label>
                    <input type="text" required value={formData.nombres} onChange={e => setFormData({...formData, nombres: e.target.value})} className="w-full p-2 border rounded bg-slate-50 outline-none focus:border-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">Apellidos</label>
                    <input type="text" required value={formData.apellidos} onChange={e => setFormData({...formData, apellidos: e.target.value})} className="w-full p-2 border rounded bg-slate-50 outline-none focus:border-blue-500" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Correo Electrónico (Usuario)</label>
                  <input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full p-2 border rounded bg-slate-50 outline-none focus:border-blue-500" />
                </div>
                
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Contraseña Temporal</label>
                  <input type="text" required value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} placeholder="Ej: Docente2026*" className="w-full p-2 border rounded bg-slate-50 outline-none focus:border-blue-500" />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Nivel de Acceso (Rol)</label>
                  <select value={formData.rol} onChange={e => setFormData({...formData, rol: e.target.value})} className="w-full p-2 border rounded bg-slate-50 outline-none focus:border-blue-500 font-medium">
                    <optgroup label="Dirección y Administración">
                      <option value="Director">👑 Director (Acceso Total + Reportes)</option>
                      <option value="Admin">⚙️ Administrador del Sistema</option>
                      <option value="Administrativo">📁 Personal Administrativo (Caja y Matrículas)</option>
                    </optgroup>
                    <optgroup label="Área Académica">
                      <option value="Docente">👨‍🏫 Docente (Notas y Asistencia de sus cursos)</option>
                      <option value="Tutor">🤝 Tutor (Supervisión de su aula asignada)</option>
                      <option value="Auxiliar">📋 Auxiliar (Control de Asistencia General)</option>
                    </optgroup>
                  </select>
                </div>

                <button type="submit" className="w-full bg-slate-800 text-white p-2.5 rounded hover:bg-slate-900 font-medium mt-2">Crear Cuenta</button>
              </form>
            </div>
          )}

          {/* Tabla de Personal */}
          <div className={`${puedeAdministrar ? 'lg:col-span-2' : 'lg:col-span-3'} bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden`}>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b text-sm text-slate-600">
                  <th className="p-4 font-medium">Nombre Completo</th>
                  <th className="p-4 font-medium">Usuario / Correo</th>
                  <th className="p-4 font-medium">Rol</th>
                  <th className="p-4 font-medium text-center">Estado</th>
                  {puedeAdministrar && <th className="p-4 text-center font-medium">Acción</th>}
                </tr>
              </thead>
              <tbody>
                {personal.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-slate-50">
                    <td className="p-4 font-medium text-slate-800">{user.apellidos}, {user.nombres}</td>
                    <td className="p-4 text-slate-600 text-sm">{user.email}</td>
                    <td className="p-4">
                      <span className="px-2 py-1 rounded text-xs font-bold bg-blue-100 text-blue-700">
                        {user.rol}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${user.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                        {user.activo ? 'Activo' : 'Suspendido'}
                      </span>
                    </td>
                    {puedeAdministrar && (
                      <td className="p-4 text-center flex justify-center gap-2">
                        <button 
                          onClick={() => handleToggleEstado(user.id, user.activo)} 
                          className={`p-2 rounded transition-colors ${user.activo ? 'text-amber-500 hover:bg-amber-50' : 'text-emerald-500 hover:bg-emerald-50'}`} 
                          title={user.activo ? "Suspender Usuario" : "Activar Usuario"}
                        >
                          {user.activo ? <PowerOff size={18} /> : <Power size={18} />}
                        </button>
                        <button onClick={() => handleEliminar(user.id)} className="text-red-500 hover:bg-red-50 p-2 rounded transition-colors" title="Eliminar Definitivamente">
                          <Trash2 size={18} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Personal;